import { Confetti } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';

export interface CelebrationProps {
  show: boolean;
  title: string;
  subtitle?: string;
  onDone: () => void;
  /** How long it stays (ms). Proportional feedback: ≤ 2.5 s, never blocks. */
  duration?: number;
}

/**
 * The big moment when a routine is complete. Does not steal focus, closes itself, can be tapped
 * away. With reduced motion it simply appears – no bounce.
 */
export function Celebration({ show, title, subtitle, onDone, duration = 2400 }: CelebrationProps) {
  useEffect(() => {
    if (!show) return;
    const t = window.setTimeout(onDone, duration);
    return () => window.clearTimeout(t);
  }, [show, duration, onDone]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-0 z-40 grid place-items-center p-gutter"
    >
      {show && (
        <button
          type="button"
          onClick={onDone}
          className={cn(
            'pointer-events-auto flex max-w-narrow flex-col items-center gap-gap-md rounded-sheet px-inset-xl py-inset-lg text-center',
            'border-(length:--shh-card-border) border-border-card bg-accent-primary text-accent-on-accent shadow-floating',
            'animate-celebrate cursor-pointer outline-none focus-visible:outline-3 focus-visible:outline-border-focus',
          )}
        >
          <Icon icon={Confetti} size="xl" weight="fill" />
          <span className="font-display text-3xl font-black tracking-tight">{title}</span>
          {subtitle && <span className="text-lg font-semibold">{subtitle}</span>}
        </button>
      )}
    </div>
  );
}
